import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { IconEbene, IconRaum, IconSuche, IconOk, IconWarnung } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, PropertyGrid, StatusBadge, WertEinheit,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Anforderung TT-Platten Rauchmelder — raumweise nach DIN VDE 0833-2.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER AUFBAU FOLGT DER HEIZUNG
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Links die Gliederung, rechts der gewählte Raum. Dasselbe Muster wie im
 * Gewerk Heizung und Kälte, und aus demselben Grund: Bei 1.021 Räumen ist
 * eine flache Liste nicht benutzbar, und ein Baum über die ganze Breite mit
 * dem Inhalt darunter zwingt zum Rollen zwischen Auswahl und Anzeige.
 *
 * Übernommen wird von dort:
 *
 *   - der Umschalter für die Gliederung (dort Räume/Netze, hier Bereich/Geschoss)
 *   - das Auswahlfeld darunter als zweiter Filter
 *   - die Suche, die den Baum IM BROWSER filtert: Er trägt nur Struktur und
 *     Zahlen, und eine Abfrage je Tastendruck wäre verschwendet
 *   - die Klassen baum / baum-ebene / baum-zeile / aktiv
 *   - Kennzahlen über dem Baum
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DREI STUFEN: EBENE → ABSCHNITT → RAUM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * So gefordert, und es entspricht der Arbeitsweise: Man nimmt sich ein
 * Geschoss vor, darin einen Abschnitt, und arbeitet dessen Räume ab.
 *
 * Der Raum ist das Blatt — er ist die Einheit, in der die Norm rechnet und in
 * der montiert wird. Die Zwischenstufe trägt die Summe der fehlenden Melder,
 * damit man nicht jeden Knoten öffnen muss, um zu finden, wo etwas fehlt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS DIE SEITE NICHT BEHAUPTET
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Solange die Deckenfeldgröße nicht hinterlegt ist, rechnet der Lauf ohne die
 * Unterteilung. Jede Zahl ist dann die UNTERE Schranke. Das steht oben und
 * nicht in einer Fußnote: Eine zu kleine Melderzahl ist ein Sicherheitsmangel,
 * und sie sieht genauso aus wie eine richtige.
 */
export function ElektroMelderbedarf() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [suche, setSuche] = useState('');
  const [befund, setBefund] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [zeigeVorgaben] = useState(false);

  useEffect(() => {
    if (!projekt) return;

    api.elektroMelderbedarf(projekt.id, { befund: befund || undefined })
      .then((x) => { setD(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id, befund]);

  // Gemerkt (Paket U6b): Ein bei jedem Durchlauf neu erzeugtes Feld treibt
  // jeden Effekt und jeden `useMemo` an, der daran hängt.
  const zeilen: any[] = useMemo(() => d?.zeilen ?? [], [d]);

  // ── Der Baum ────────────────────────────────────────────────────────────
  //
  // Aus den Zeilen gebaut und nicht vom Server geholt: Es sind gut tausend
  // Zeilen mit je einem Dutzend Zahlen — wenige hundert Kilobyte. Eine zweite
  // Abfrage nur für die Struktur wäre ein Weg, auf dem beide auseinanderlaufen
  // können.
  const baum = useMemo(() => {
    const treffer = suche.trim().toLowerCase();

    const passt = (z: any) =>
      !treffer
      || (z.raumnummer ?? '').toLowerCase().includes(treffer)
      || (z.raumname ?? '').toLowerCase().includes(treffer);

    // Ebene → Abschnitt → Raum. Zwei verschachtelte Karten statt einer
    // Gruppierung über einen zusammengesetzten Schlüssel: Die Zwischenstufe
    // braucht eigene Summen, und die ließen sich aus einem flachen
    // Gruppenschlüssel nur wieder herausrechnen.
    const ebenen = new Map<string, Map<string, any[]>>();

    for (const z of zeilen) {
      if (!passt(z)) continue;

      const ebene = z.geschoss ?? 'ohne Geschoss';
      const abschnitt = z.bearbeitungsbereich ?? 'ohne Abschnitt';

      if (!ebenen.has(ebene)) ebenen.set(ebene, new Map());

      const a = ebenen.get(ebene)!;

      if (!a.has(abschnitt)) a.set(abschnitt, []);
      a.get(abschnitt)!.push(z);
    }

    const summe = (zs: any[]) => ({
      erforderlich: zs.reduce((s, k) => s + (k.melderErforderlich ?? 0), 0),
      vorhanden: zs.reduce((s, k) => s + k.melderVorhanden, 0),
      fehlend: zs.reduce((s, k) => s + Math.max(0, k.differenz ?? 0), 0),
    });

    return [...ebenen.entries()]
      .map(([ebene, abschnitte]) => {
        const kinder = [...abschnitte.entries()]
          .map(([name, zs]) => ({
            name,
            schluessel: `${ebene}/${name}`,
            raeume: zs.sort((a, b) =>
              (a.raumnummer ?? '').localeCompare(b.raumnummer ?? '')),
            ...summe(zs),
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        return {
          name: ebene,
          kinder,
          raeume: kinder.reduce((s, k) => s + k.raeume.length, 0),
          ...summe([...abschnitte.values()].flat()),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [zeilen, suche]);

  const raum = useMemo(
    () => zeilen.find((z) => z.id === gewaehlt) ?? null,
    [zeilen, gewaehlt]);

  const felder: any[] = useMemo(() => d?.deckenfelder ?? [], [d]);

  const offeneFeldgroesse = useMemo(
    () => felder.length === 0
       && !(d?.vorgaben ?? [])
            .find((v: any) => v.schluessel === 'deckenfeld_m2')?.zahlwert,
    [d, felder]);

  const kopf = (
    <PageHeader
      titel="Anforderung TT-Platten Rauchmelder"
      beschreibung={'Raumweiser Bedarf an punktförmigen Rauchmeldern nach '
        + 'DIN VDE 0833-2, Abschnitt 6.2.7.4 — die Decke ist durch die Unterzüge '
        + 'der TT-Platten in Deckenfelder unterteilt, und die Norm behandelt diese '
        + 'Felder gesondert.'}
      meta={d?.summe
        ? `${(d.summe.mitAnforderung ?? 0).toLocaleString('de-DE')} Räume mit Brandmelderanforderung`
        : undefined}
      aktionen={(
        // W2-D1: Steuermatrix, Notstrom und Ringnachweis auf der Nachbarseite.
        <Link className="m-knopf" to="../elektro-sicherheitstechnik" relative="path">
          Brandfallsteuerung und Notstrom
        </Link>
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Der Melderbedarf konnte nicht geladen werden."
          technisch={fehler}
        />
      </>
    );
  }

  if (!projekt || !d) {
    return (
      <>
        {kopf}
        <LoadingState text="Melderbedarf wird geladen …" zeilen={10} spalten={5} />
      </>
    );
  }

  const s = d.summe ?? {};

  return (
    <>
      {kopf}

      {/* Der Vorbehalt steht OBEN. Eine zu kleine Melderzahl sieht aus wie
          eine richtige. */}
      {offeneFeldgroesse && (
        <div className="hinweis warnung" style={{ display: 'block', maxWidth: 820 }}>
          <strong>Die Deckenfeldgröße ist nicht hinterlegt.</strong> Gerechnet
          wird ohne die Unterteilung durch die Unterzüge — jede Melderzahl auf
          dieser Seite ist damit die <strong>untere Schranke</strong> und nicht
          die Antwort. Mit Deckenfeldern nach 6.2.7.4 werden es mehr, nie
          weniger. Die Feldgröße folgt aus Rippenabstand mal Spannweite und ist
          beim Tragwerksplaner bekannt; sie wird als Vorgabe{' '}
          <code>deckenfeld_m2</code> hinterlegt.
        </div>
      )}

      {/* --- Kennzahlen über dem Baum, wie bei der Heizung -------------- */}
      <KpiReihe>
        <KpiCard titel="Melder erforderlich" wert={s.erforderlich ?? 0}
                 fussnote="DIN VDE 0833-2" />
        <KpiCard titel="im Modell vorhanden" wert={s.vorhanden ?? 0} />
        <KpiCard
          titel="fehlen in einzelnen Räumen" wert={s.fehlend ?? 0}
          zustand={(s.fehlend ?? 0) > 0 ? 'fehler' : 'gut'}
          zustandText={(s.fehlend ?? 0) > 0
            ? 'Sicherheitsmangel' : 'kein Raum unterversorgt'}
        />
        <KpiCard titel="überzählig in anderen" wert={s.ueberzaehlig ?? 0} />
      </KpiReihe>

      <div className="karte">
        <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 10 }}>
          Fehlend und überzählig heben sich <strong>nicht</strong> auf: Ein
          Melder zu viel im Flur ersetzt keinen fehlenden im Technikraum.
          Gerechnet wird für die {(s.mitAnforderung ?? 0).toLocaleString('de-DE')}{' '}
          Räume, für die das Raumbuch eine Brandmelderanforderung führt.
        </p>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DIE DECKENFELDER UND WOHER SIE KOMMEN

          Sie ist die Größe, die die Melderzahl am stärksten bewegt — und die
          einzige, die nicht in der Norm steht, sondern aus dem Modell
          hergeleitet ist. Deshalb steht die Herleitung auf der Seite und
          nicht nur in der Datenbank.
          ═══════════════════════════════════════════════════════════════ */}
      {felder.length > 0 && (
        <ContentSection titel="Deckenfelder der TT-Platten — je Abschnitt hergeleitet" nackt>
          <DataTable
            name="Deckenfelder"
            spalten={[
              {
                schluessel: 'abschnitt', titel: 'Abschnitt', immerSichtbar: true,
                wert: (f: any) => f.planungsbereich ?? 'ohne Abschnitt',
                inhalt: (f: any) => (
                  <span title={f.herleitung ?? ''}>
                    {f.planungsbereich ?? 'ohne Abschnitt'}
                  </span>
                ),
              },
              { schluessel: 'platten', titel: 'Platten', art: 'zahl', wert: (f: any) => f.platten },
              { schluessel: 'breite', titel: 'Plattenbreite', art: 'zahl', einheit: 'mm', wert: (f: any) => f.plattenbreiteMm },
              { schluessel: 'rippen', titel: 'Rippen je Platte', art: 'zahl', wert: (f: any) => f.rippenJePlatte },
              { schluessel: 'rippenabstand', titel: 'Rippenabstand', art: 'zahl', einheit: 'mm', wert: (f: any) => f.rippenabstandMm },
              { schluessel: 'spannweite', titel: 'Spannweite', art: 'zahl', einheit: 'mm', wert: (f: any) => f.spannweiteMm },
              { schluessel: 'bauhoehe', titel: 'Bauhöhe', art: 'zahl', einheit: 'mm', wert: (f: any) => f.bauhoehe },
              {
                schluessel: 'feld', titel: 'Deckenfeld', art: 'zahl', einheit: 'm²',
                nachkomma: 1, wert: (f: any) => f.feldflaecheM2,
                inhalt: (f: any) => <strong><WertEinheit wert={f.feldflaecheM2 ?? null} nachkomma={1} rechts /></strong>,
              },
            ]}
            zeilen={felder}
            schluessel={(f: any) => String(f.planungsbereich ?? 'ohne')}
            suche={false}
            ausfuhr
            ausfuhrDateiname="deckenfelder"
            dicht
          />

          <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8 }}>
            Die <strong>Plattenbreite</strong> stammt aus dem Typnamen
            (<code>TRG-TT-PL-2990/560-STB</code> → 2990 mm breit, 560 mm hoch),
            als Median über die Platten des Abschnitts. Die{' '}
            <strong>Spannweite</strong> ist der Median der langen Abmessung —
            Median und nicht Mittelwert, weil eine gedrehte Platte einen zu
            großen Umrissquader mitbringt.
          </p>

          <p className="hinweis" style={{ display: 'block', fontSize: 12 }}>
            <strong>Der Rippenabstand ist eine Annahme.</strong> Eine TT-Platte
            ist eine Doppelstegplatte, also zwei Rippen je Element und damit
            der halbe Plattenabstand. Aus der Geometrie ist das nicht
            nachprüfbar: Der Umrissquader zeigt die Platte, nicht ihre Stege.
            Wer einstegige Elemente hat, ändert die Vorgabe{' '}
            <code>rippen_je_platte</code>.
          </p>
        </ContentSection>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          DIE SONDERKONZEPTE STEHEN VOR DER MELDERLISTE

          Sie sind KEINE Lücke, sondern eine andere Lösung. Stünden sie nur
          als Befund in einer Zeile, läsen sich 79 Bowl-Räume und 70
          Aufzugsschächte wie ungelöste Fälle — und jemand bestellte
          Punktmelder für einen Schacht, der ein Ansaugsystem bekommt.
          ═══════════════════════════════════════════════════════════════ */}
      {(d.jeKonzept ?? []).length > 0 && (
        <ContentSection titel="Bereiche mit gesondertem Brandmeldekonzept" nackt>
          <DataTable
            name="Gesonderte Brandmeldekonzepte"
            spalten={[
              {
                schluessel: 'bereich', titel: 'Bereich', immerSichtbar: true,
                wert: (k: any) => ({
                  ras: 'Aufzugsschächte',
                  ras_hlks: 'HLSK-Schächte',
                  ansaug_oder_linienfoermig: 'Bowl — Ränge, Eventfloor, Bühne',
                } as Record<string, string>)[k.konzeptart] ?? k.konzeptart,
                inhalt: (k: any) => (
                  <strong>{({
                    ras: 'Aufzugsschächte',
                    ras_hlks: 'HLSK-Schächte',
                    ansaug_oder_linienfoermig: 'Bowl — Ränge, Eventfloor, Bühne',
                  } as Record<string, string>)[k.konzeptart] ?? k.konzeptart}</strong>
                ),
              },
              {
                schluessel: 'verfahren', titel: 'Verfahren',
                wert: (k: any) => ({
                  ras: 'Rauchansaugsystem (RAS)',
                  ras_hlks: 'Rauchansaugsystem (RAS)',
                  ansaug_oder_linienfoermig: 'Ansaugrauchmelder oder linienförmige Melder',
                } as Record<string, string>)[k.konzeptart] ?? '—',
              },
              {
                schluessel: 'anlagen', titel: 'Anlagen', art: 'zahl',
                wert: (k: any) => k.anlagen,
                inhalt: (k: any) => (
                  <>
                    <strong>{k.anlagen}</strong>
                    {k.zugeordnet != null && k.zugeordnet !== k.anlagen && (
                      <span className="sehr-schwach" style={{ marginLeft: 4 }}
                            title="über die Lage einem Schacht zugeordnet">
                        ({k.zugeordnet} zugeordnet)
                      </span>
                    )}
                  </>
                ),
              },
              { schluessel: 'raeume', titel: 'Räume im Modell', art: 'zahl', wert: (k: any) => k.raeume },
              { schluessel: 'flaeche', titel: 'Fläche', art: 'zahl', einheit: 'm²', wert: (k: any) => k.flaecheM2 },
              { schluessel: 'vorhanden', titel: 'Melder im Modell', art: 'zahl', wert: (k: any) => k.vorhanden },
            ]}
            zeilen={d.jeKonzept}
            schluessel={(k: any) => String(k.konzeptart)}
            suche={false}
            dicht
          />

          <p className="hinweis" style={{ display: 'block', fontSize: 13, marginTop: 8 }}>
            Für diese Bereiche wird <strong>keine punktförmige Sollzahl
            gerechnet</strong> — sie sind damit auch nicht in „fehlend"
            enthalten.
            <br /><br />
            <strong>Bowl:</strong> im Mittel 25 m hoch. Tabelle 2 der
            DIN VDE 0833-2 endet bei 12 m; darüber ist die Norm selbst
            heranzuziehen, und eine fortgeschriebene Tabellenzeile wäre
            geraten.
            <br /><br />
            <strong>Aufzugsschächte:</strong> Der Schacht steht im Modell als
            ein Raum <em>je Geschoss</em>, ist aber ein durchgehendes Volumen —
            die Spalte <em>Anlagen</em> zählt die Schächte, die Spalte
            <em>Räume im Modell</em> ihre Geschossabschnitte. Ein Ansaugsystem
            erfasst einen Schacht über die ganze Höhe mit einer Rohrleitung;
            die punktförmige Rechnung forderte zuvor einen Melder je
            Geschossabschnitt.
            <br /><br />
            <strong>HLSK-Schächte:</strong> dieselbe Bauform. 58 Raumzeilen
            im Modell tragen alle denselben Namen; es sind acht Schächte nach
            Angabe des Fachplaners. Über die Lage aus der Aussparungsaufnahme
            ließen sich sieben davon identifizieren — 28 der 58 Räume tragen
            keine Lage, und der achte ist unter ihnen. Deshalb steht die
            gemeldete Zahl vorn und die zugeordnete daneben: Beides ist wahr.
            <br /><br />
            Die vorhandenen Melder bleiben sichtbar. Die Auslegung der Systeme
            ist eine eigene Aufgabe und steht hier nicht.
          </p>
        </ContentSection>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          NICHT JEDER RAUM LIEGT UNTER TT-PLATTEN

          Gemeldet am 18.08.2026. Die Unterscheidung ist keine Feinheit: Die
          Unterteilung nach 6.2.7.4 ERLEICHTERT (Fall a erlaubt einem Melder
          das 1,2fache der Überwachungsfläche). Wer sie einem Raum unter
          Ortbeton zuspricht, rechnet ihm Melder weg, die er braucht.
          ═══════════════════════════════════════════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════
          ZWEIMELDUNGSABHÄNGIGKEIT

          Sie steht im Raumbuchfeld `falschalarm` — Einzahl. Unter
          `falschalarme` steht für alle 1.023 Räume nichts; das war der
          erste Anlauf und er sah aus wie „gibt es hier nicht".

          Die Karte trägt eine Warnung, und die ist wichtiger als die
          Zahlen darunter: WELCHER Typ einen zweiten Melder verlangt, ist
          in diesem Projekt nirgends festgelegt. Die Zuordnung entscheidet
          über 669 gegen 125 Räume.
          ═══════════════════════════════════════════════════════════════ */}
      {(d.jeAbhaengigkeit ?? []).length > 0 && (
        <ContentSection
          titel="Zweimeldungsabhängigkeit"
          meta={<StatusBadge zustand="warnung" text="Noch nicht freigegeben" />}
        >
          <div className="hinweis" style={{ display: 'block', borderColor: 'var(--schlecht)' }}>
            <strong>Noch nicht freigegeben.</strong> Angesetzt ist die
            übliche Lesart der DIN&nbsp;VDE&nbsp;0833-2: <em>Typ A</em> gilt
            auch als erfüllt, wenn derselbe Melder nach Rückstellung erneut
            anspricht — daraus folgt keine Mindestzahl. <em>Typ B</em>
            verlangt die Meldung eines <strong>zweiten</strong> Melders; ein
            Raum mit nur einem könnte dort keinen Alarm auslösen, also sind
            zwei Pflicht. Diese Zuordnung steht in keinem Dokument dieses
            Projekts und gehört vom Fachplaner bestätigt. Sie liegt als
            Parameter in <code>elektro_melder_vorgaben</code> und lässt sich
            ohne neue Programmfassung umlegen.
          </div>

          <DataTable
            name="Zweimeldungsabhängigkeit"
            spalten={[
              { schluessel: 'art', titel: 'Art', immerSichtbar: true, wert: (g: any) => g.art },
              { schluessel: 'raeume', titel: 'Räume', art: 'zahl', wert: (g: any) => g.raeume },
              {
                schluessel: 'hebtAn', titel: 'hebt die Zahl an', art: 'zahl',
                titelHinweis: 'Aus der Fläche ergäbe sich ein Melder, wegen der Abhängigkeit werden zwei gesetzt',
                wert: (g: any) => g.hebtAn,
                inhalt: (g: any) => (g.hebtAn > 0 ? <strong>{g.hebtAn}</strong> : <>{g.hebtAn}</>),
              },
              { schluessel: 'erforderlich', titel: 'erforderlich', art: 'zahl', wert: (g: any) => g.erforderlich },
            ]}
            zeilen={d.jeAbhaengigkeit}
            schluessel={(g: any) => String(g.art)}
            suche={false}
            dicht
          />

          <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8 }}>
            „Hebt die Zahl an" heißt: Aus der Fläche ergäbe sich <em>ein</em>
            Melder, wegen der Abhängigkeit werden <strong>zwei</strong>
            gesetzt. In allen übrigen Räumen liegt die Flächenrechnung
            ohnehin bei zwei oder mehr — die Abhängigkeit ändert dort nichts.
          </p>
        </ContentSection>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          ZWISCHENDECKENMELDER

          Zwei Aussagen, und die zweite ist die unangenehme:

          1. Sie fehlen vollständig. Nicht „wir finden keine" — sie lassen
             sich im Modell gar nicht erkennen, weil Raum- und
             Hohlraummelder dieselbe Bauteilart tragen.

          2. Die Hohlräume sind zu flach. In 580 von 593 Räumen liegt der
             Hohlraum unter 800 mm, meist zwischen 60 und 430 mm. Dort ist
             die Überwachung weder von der Norm gefordert noch baubar.
          ═══════════════════════════════════════════════════════════════ */}
      {(d.jeHohlraum ?? []).length > 0 && (
        <ContentSection
          titel="Zwischendeckenmelder"
          beschreibung={'Gerechnet eins zu eins zu den erforderlichen Raummeldern, wie '
            + 'beauftragt — nicht nach Fläche. Wo ein Raummelder unter einer '
            + 'Abhangdecke sitzt, gehört ein Zwischendeckenmelder darüber.'}
        >
          <DataTable
            name="Zwischendeckenmelder"
            spalten={[
              {
                schluessel: 'befund', titel: 'Befund', art: 'status', breite: '200px',
                wert: (g: any) => ({
                  erforderlich: 'erforderlich',
                  hohlraum_zu_flach: 'Hohlraum zu flach',
                  keine_hoehe: 'Höhe unbekannt',
                } as Record<string, string>)[g.befund] ?? g.befund,
                inhalt: (g: any) => (
                  <StatusBadge
                    zustand={g.befund === 'erforderlich' ? 'info' : 'warnung'}
                    text={({
                      erforderlich: 'erforderlich',
                      hohlraum_zu_flach: 'Hohlraum zu flach',
                      keine_hoehe: 'Höhe unbekannt',
                    } as Record<string, string>)[g.befund] ?? g.befund}
                    klein
                  />
                ),
              },
              { schluessel: 'raeume', titel: 'Räume', art: 'zahl', wert: (g: any) => g.raeume },
              { schluessel: 'erforderlich', titel: 'erforderlich', art: 'zahl', wert: (g: any) => g.erforderlich },
              {
                schluessel: 'hohlraum', titel: 'Hohlraum', einheit: 'mm',
                ausrichtung: 'rechts', wert: (g: any) => g.hoeheMin,
                inhalt: (g: any) => (
                  <>{g.hoeheMin == null ? '—'
                    : g.hoeheMin === g.hoeheMax
                      ? Math.round(g.hoeheMin)
                      : `${Math.round(g.hoeheMin)}–${Math.round(g.hoeheMax)}`}</>
                ),
              },
              {
                schluessel: 'bedeutung', titel: 'Bedeutung',
                wert: (g: any) => ({
                  erforderlich: 'Hohlraum ab 800 mm — nach DIN VDE 0833-2 zu überwachen.',
                  hohlraum_zu_flach: 'Unter 800 mm. Die Norm verlangt hier keine '
                    + 'Überwachung, und in diese Höhe passt kein punktförmiger Melder. '
                    + 'Die Zahl steht wie beauftragt — ob dort gebaut werden kann, '
                    + 'gehört vor der Bestellung geklärt.',
                  keine_hoehe: 'Die Höhe kommt aus koll_hoehenbaender je Geschoss und '
                    + 'Abschnitt; fehlt eines von beiden am Raum, gibt es keinen Wert.',
                } as Record<string, string>)[g.befund] ?? '',
                inhalt: (g: any) => (
                  <span className="sehr-schwach" style={{ fontSize: 12 }}>
                    {({
                      erforderlich: 'Hohlraum ab 800 mm — nach DIN VDE 0833-2 zu überwachen.',
                      hohlraum_zu_flach: 'Unter 800 mm. Die Norm verlangt hier keine '
                        + 'Überwachung, und in diese Höhe passt kein punktförmiger Melder. '
                        + 'Die Zahl steht wie beauftragt — ob dort gebaut werden kann, '
                        + 'gehört vor der Bestellung geklärt.',
                      keine_hoehe: 'Die Höhe kommt aus koll_hoehenbaender je Geschoss und '
                        + 'Abschnitt; fehlt eines von beiden am Raum, gibt es keinen Wert.',
                    } as Record<string, string>)[g.befund] ?? ''}
                  </span>
                ),
              },
            ]}
            zeilen={d.jeHohlraum}
            schluessel={(g: any) => String(g.befund)}
            suche={false}
            dicht
          />

          <div className="hinweis" style={{ display: 'block', borderColor: 'var(--schlecht)',
                                            marginTop: 10 }}>
            <strong>Vorhanden: keine.</strong> Und das heißt hier nicht
            „gezählt und null gefunden", sondern <em>nicht erkennbar</em>:
            Raum- und Hohlraummelder tragen im Modell dieselbe Bauteilart
            <code> rauchmelder</code>, und kein Typname unterscheidet sie.
            Die Höhe tut es auch nicht — gemessen streuen die Melder um
            ±1&nbsp;m um die Oberkante der Abhangdecke (Median −50&nbsp;mm),
            weil Abhangdecken gestuft sind und die Einbaulage nicht die
            Sensorfläche ist. Eine Trennung nach dieser Zahl hätte
            1.089&nbsp;Melder zu Hohlraummeldern erklärt, ein Drittel des
            Bestandes. Sobald sie mit eigener Bauteilart oder eigenem Typ
            gesetzt werden, erkennt die Auswertung sie daran.
          </div>
        </ContentSection>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          WOHER DIE RAUMZUORDNUNG JEDES MELDERS KOMMT

          Ohne diese Karte liest sich "3.024 vorhanden" wie eine gezaehlte
          Zahl. Sie ist es zum groessten Teil auch -- aber 87 Melder haben
          ihren Raum nicht, weil sie darin stehen, sondern weil unter ihnen
          etwas steht, das darin steht.

          Und 79 tauchen ueberhaupt nicht auf. Das ist die wichtigere Zeile:
          Sie sind weder als vorhanden noch als fehlend gezaehlt, und wer das
          nicht weiss, haelt die Fehlliste fuer vollstaendig.
          ═══════════════════════════════════════════════════════════════ */}
      {(d.verortung ?? []).length > 0 && (
        <ContentSection titel="Woher die Raumzuordnung der Melder kommt" nackt>
          <DataTable
            name="Raumzuordnung"
            spalten={[
              {
                schluessel: 'herkunft', titel: 'Herkunft', immerSichtbar: true,
                wert: (v: any) => ({
                  lage: 'an der Lage gemessen',
                  nachbar: 'Nachbarraum',
                  lage_darunter: 'aus dem Raum darunter geschlossen',
                  ohne: 'kein Raum',
                } as Record<string, string>)[v.herkunft] ?? v.herkunft,
              },
              {
                schluessel: 'anzahl', titel: 'Melder', art: 'zahl',
                wert: (v: any) => v.anzahl,
                inhalt: (v: any) => (v.herkunft === 'ohne'
                  ? <StatusBadge zustand="fehler" text={String(v.anzahl)} klein
                                 titel="weder als vorhanden noch als fehlend gezählt" />
                  : <>{v.anzahl}</>),
              },
              {
                schluessel: 'erklaerung', titel: 'Was das heißt',
                wert: (v: any) => v.erklaerung,
                inhalt: (v: any) => (
                  <span className="sehr-schwach" style={{ fontSize: 12 }}>{v.erklaerung}</span>
                ),
              },
            ]}
            zeilen={d.verortung}
            schluessel={(v: any) => String(v.herkunft)}
            suche={false}
            dicht
          />

          <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8 }}>
            Die Zuordnung <strong>aus dem Raum darunter</strong> ist ein
            Schluss und keine Messung: Steht unter einem Melder ohne Raum in
            höchstens 3&nbsp;m waagerecht und bis 4&nbsp;m tiefer ein bereits
            verortetes Bauteil, gilt dessen Raum. Kommen mehrere in Frage,
            gewinnt der nächste. Nachgemessen an den 87&nbsp;Fällen: 53 liegen
            im selben Geschoss wie ihr Raum, 33 genau eines höher — der
            Deckenhohlraum — und keiner weiter weg.
          </p>
        </ContentSection>
      )}

      {(d.jeDeckentyp ?? []).length > 0 && (
        <ContentSection titel="Was über den Räumen liegt" nackt>
          <DataTable
            name="Deckentypen"
            spalten={[
              {
                schluessel: 'decke', titel: 'Decke', immerSichtbar: true,
                wert: (g: any) => ({
                  tt: 'TT-Platte', andere: 'andere Decke', ausgeklammert: 'Eventfloor',
                  unbekannt: 'nicht verortbar', aussen: 'Außenfläche',
                  sonderkonzept: 'gesondertes Konzept',
                } as Record<string, string>)[g.deckentyp] ?? g.deckentyp,
              },
              { schluessel: 'raeume', titel: 'Räume', art: 'zahl', wert: (g: any) => g.raeume },
              { schluessel: 'erforderlich', titel: 'erforderlich', art: 'zahl', wert: (g: any) => g.erforderlich },
              { schluessel: 'vorhanden', titel: 'vorhanden', art: 'zahl', wert: (g: any) => g.vorhanden },
              {
                schluessel: 'fehlend', titel: 'fehlend', art: 'zahl',
                wert: (g: any) => g.fehlend,
                inhalt: (g: any) => (g.fehlend > 0
                  ? <StatusBadge zustand="fehler" text={String(g.fehlend)} klein />
                  : <>{g.fehlend}</>),
              },
              {
                schluessel: 'bedeutung', titel: 'Bedeutung',
                wert: (g: any) => ({
                  tt: 'Deckenfeldunterteilung nach 6.2.7.4 gilt.',
                  andere: 'Keine TT-Platte darüber — ohne Unterteilung gerechnet, also mit der höheren Zahl.',
                  ausgeklammert: 'Auf Ansage ausgeklammert. Steht in der Liste, aber ohne die Erleichterung.',
                  unbekannt: 'Kein Elektrobauteil im Raum, dessen Lage ihn verrät. Ohne Unterteilung gerechnet.',
                  aussen: 'Im Freien — ein punktförmiger Melder braucht eine Decke, unter der sich Rauch sammelt.',
                  sonderkonzept: 'Ansaugrauchmelder oder linienförmige Melder. Keine punktförmige Sollzahl.',
                } as Record<string, string>)[g.deckentyp] ?? '',
                inhalt: (g: any) => (
                  <span className="sehr-schwach" style={{ fontSize: 12 }}>
                    {({
                      tt: 'Deckenfeldunterteilung nach 6.2.7.4 gilt.',
                      andere: 'Keine TT-Platte darüber — ohne Unterteilung gerechnet, also mit der höheren Zahl.',
                      ausgeklammert: 'Auf Ansage ausgeklammert. Steht in der Liste, aber ohne die Erleichterung.',
                      unbekannt: 'Kein Elektrobauteil im Raum, dessen Lage ihn verrät. Ohne Unterteilung gerechnet.',
                      aussen: 'Im Freien — ein punktförmiger Melder braucht eine Decke, unter der sich Rauch sammelt.',
                      sonderkonzept: 'Ansaugrauchmelder oder linienförmige Melder. Keine punktförmige Sollzahl.',
                    } as Record<string, string>)[g.deckentyp] ?? ''}
                  </span>
                ),
              },
            ]}
            zeilen={d.jeDeckentyp}
            schluessel={(g: any) => String(g.deckentyp)}
            suche={false}
            dicht
          />

          <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8 }}>
            Verortet über den <strong>Mittelwert der Lagen aller
            Elektrobauteile</strong> des Raumes — Melder, Leuchten und Dosen
            verteilen sich über die Fläche. Darüber wird in der
            Koordinationsaufnahme gesucht, ob der Umriss einer TT-Platte
            diesen Punkt einschließt. Ein Raum, der halb unter TT-Platten und
            halb unter Ortbeton liegt, wird nach seinem Mittelpunkt
            eingeordnet; für einen Nachweis gehört er angesehen.
          </p>
        </ContentSection>
      )}

      {/* --- Links die Gliederung, rechts der gewählte Raum -------------- */}
      <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
        <div className="baum">
          <div style={{ padding: '10px 12px' }}>
            <select
              value={befund}
              onChange={(e) => setBefund(e.target.value)}
              style={{ width: '100%', fontSize: 12, marginBottom: 8 }}
            >
              <option value="">alle Räume</option>
              <option value="fehlend">nur fehlende</option>
              <option value="erfuellt">nur erfüllte</option>
              <option value="ueberzaehlig">nur überzählige</option>
              <option value="anforderung_offen">Anforderung offen</option>
            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconSuche size={14} />
              <input
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Raumnummer oder Bezeichnung"
                style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
              />
            </label>
          </div>

          {baum.map((e) => (
            <div key={e.name}>
              {/* Stufe 1: die Ebene */}
              <button
                className="baum-ebene"
                onClick={() => setOffen((x) => {
                  const neu = new Set(x);
                  if (neu.has(e.name)) neu.delete(e.name); else neu.add(e.name);
                  return neu;
                })}
              >
                <IconEbene size={14} />
                <span>{e.name}</span>

                <span style={{ display: 'flex', gap: 5, marginLeft: 'auto',
                               fontSize: 10, alignItems: 'center' }}>
                  {e.fehlend > 0 && (
                    <span style={{ color: 'var(--schlecht)' }}
                          title="fehlende Melder">{e.fehlend} fehlen</span>
                  )}
                  <span className="schwach">{e.raeume}</span>
                </span>
              </button>

              {offen.has(e.name) && e.kinder.map((a: any) => (
                <div key={a.schluessel}>
                  {/* Stufe 2: der Abschnitt, eingerückt */}
                  <button
                    className="baum-ebene"
                    style={{ paddingLeft: 26 }}
                    onClick={() => setOffen((x) => {
                      const neu = new Set(x);
                      if (neu.has(a.schluessel)) neu.delete(a.schluessel);
                      else neu.add(a.schluessel);
                      return neu;
                    })}
                  >
                    <IconEbene size={12} />
                    <span>{a.name}</span>

                    <span style={{ display: 'flex', gap: 5, marginLeft: 'auto',
                                   fontSize: 10, alignItems: 'center' }}>
                      {a.fehlend > 0 && (
                        <span style={{ color: 'var(--schlecht)' }}
                              title="fehlende Melder">{a.fehlend} fehlen</span>
                      )}
                      <span className="schwach">{a.raeume.length}</span>
                    </span>
                  </button>

                  {/* Stufe 3: die Räume */}
                  {offen.has(a.schluessel) && a.raeume.map((k: any) => (
                    <button
                      key={k.id}
                      className={`baum-zeile ${gewaehlt === k.id ? 'aktiv' : ''}`}
                      style={{ paddingLeft: 42 }}
                      onClick={() => setGewaehlt(k.id)}
                      title={k.raumname ?? ''}
                    >
                      <IconRaum size={13} />

                      <span style={{ minWidth: 0, overflow: 'hidden',
                                     textOverflow: 'ellipsis',
                                     whiteSpace: 'nowrap' }}>
                        {k.raumnummer ?? '—'}
                        {k.raumname && (
                          <span className="schwach" style={{ marginLeft: 5 }}>
                            {k.raumname}
                          </span>
                        )}
                      </span>

                      <span style={{ display: 'flex', gap: 4,
                                     marginLeft: 'auto', fontSize: 10,
                                     alignItems: 'center' }}>
                        {k.differenz != null && k.differenz > 0 && (
                          <span style={{ color: 'var(--schlecht)' }}
                                title="fehlende Melder">+{k.differenz}</span>
                        )}
                        {k.differenz === 0 && <IconOk size={11} />}
                        {k.befund === 'anforderung_offen' && (
                          <IconWarnung size={11} />
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ))}

          {baum.length === 0 && (
            <EmptyState
              klein
              titel="Kein Raum trifft die Auswahl."
              erklaerung="Befundfilter und Suche schränken den Baum ein."
            />
          )}
        </div>

        {/* --- Rechts: der gewählte Raum ------------------------------- */}
        <div style={{ flex: 1, padding: '14px 16px', minWidth: 0 }}>
          {!raum ? (
            <EmptyState
              klein
              titel="Noch kein Raum gewählt."
              erklaerung={'Einen Raum in der Gliederung wählen. Die Zahlen darüber '
                + 'gelten für das ganze Projekt.'}
            />
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>
                {raum.raumnummer} — {raum.raumname}
              </h3>

              <p className="sehr-schwach" style={{ fontSize: 12 }}>
                {raum.bearbeitungsbereich ?? 'ohne Bereich'} · {raum.geschoss}
              </p>

              <KpiReihe>
                <KpiCard titel="erforderlich" wert={raum.melderErforderlich ?? null} />
                <KpiCard titel="vorhanden" wert={raum.melderVorhanden} />
                <KpiCard
                  titel="Differenz"
                  wert={raum.differenz == null ? null
                    : raum.differenz > 0 ? `+${raum.differenz}` : raum.differenz}
                  zustand={(raum.differenz ?? 0) > 0 ? 'fehler'
                    : raum.differenz === 0 ? 'gut' : undefined}
                  zustandText={(raum.differenz ?? 0) > 0 ? 'Melder fehlen'
                    : raum.differenz === 0 ? 'erfüllt' : undefined}
                />
              </KpiReihe>

              <PropertyGrid
                spalten={2}
                eintraege={[
                  { beschriftung: 'Fläche', wert: raum.flaecheM2, einheit: 'm²', nachkomma: 1, herkunft: 'modell' },
                  {
                    beschriftung: 'Raumhöhe', wert: raum.raumhoeheM, einheit: 'm',
                    nachkomma: 2, herkunft: 'modell',
                    hinweis: `aus ${raum.hoeheHerkunft}`,
                  },
                  { beschriftung: 'Anforderung Raumbuch', wert: raum.anforderungRaumbuch ?? null, herkunft: 'daten' },
                  {
                    beschriftung: 'Überwachungsfläche je Melder',
                    wert: raum.ueberwachungsflaecheM2 ?? null, einheit: 'm²',
                    herkunft: 'baugruppe',
                  },
                  {
                    beschriftung: 'Größter Melderabstand', wert: raum.abstandswertM ?? null,
                    einheit: 'm', herkunft: 'baugruppe',
                    hinweis: 'horizontal, Bild 3 der Norm',
                  },
                  {
                    beschriftung: 'Decke über dem Raum',
                    wert: ({
                      tt: 'TT-Platte',
                      andere: 'andere Decke — keine Unterteilung',
                      ausgeklammert: 'Eventfloor, ausgeklammert',
                      unbekannt: 'nicht verortbar',
                      aussen: 'Außenfläche',
                      sonderkonzept: raum.konzeptart === 'ras'
                        ? 'gesondertes Konzept — Rauchansaugsystem (RAS)'
                        : 'gesondertes Konzept — Ansaugrauchmelder oder linienförmige Melder',
                    } as Record<string, string>)[raum.deckentyp] ?? (raum.deckentyp ?? null),
                    hinweis: raum.lageX != null
                      ? `verortet bei ${raum.lageX.toFixed(1)} / ${raum.lageY?.toFixed(1)} m`
                      : undefined,
                    breit: true,
                  },
                  {
                    beschriftung: 'Deckenfeld', wert: raum.deckenfeldM2 ?? null,
                    einheit: 'm²', nachkomma: 1,
                    hinweis: raum.feldHerkunft === 'tt_platten'
                      ? 'aus den TT-Platten des Abschnitts'
                      : raum.feldHerkunft === 'vorgabe'
                        ? 'aus der Vorgabe deckenfeld_m2'
                        : 'nicht hinterlegt',
                  },
                  { beschriftung: 'Fall nach 6.2.7.4', wert: raum.fall ?? null },
                  ...(raum.melderOhneFelder != null ? [{
                    beschriftung: 'ohne Deckenfelder wären es',
                    wert: raum.melderOhneFelder, einheit: 'Melder',
                    hinweis: raum.melderErforderlich != null
                      && raum.melderOhneFelder !== raum.melderErforderlich
                      ? `die Unterteilung ändert das Ergebnis um ${raum.melderErforderlich - raum.melderOhneFelder}`
                      : undefined,
                  }] : []),
                  ...(raum.felderImRaum != null ? [{
                    beschriftung: 'Deckenfelder im Raum', wert: raum.felderImRaum,
                    hinweis: raum.felderJeMelder != null
                      ? `${raum.felderJeMelder} Felder je Melder` : undefined,
                  }] : []),
                  { beschriftung: 'Befund', wert: raum.befund ?? null },
                ]}
              />

              {/* Die vollständige Rechnung im Klartext. Wer eine Melderzahl
                  anzweifelt, soll sie nachrechnen können, ohne den Code zu
                  lesen. */}
              {raum.begruendung && (
                <p className="hinweis" style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
                  {raum.begruendung}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* --- Die Normwerte, auf Wunsch ---------------------------------- */}
      <ContentSection
        titel="Normwerte"
        aufklappbar
        standardOffen={zeigeVorgaben}
        beschreibung={'Keiner davon ist geprüft. Sie stammen aus einer Wiedergabe der '
          + 'DIN VDE 0833-2 und nicht aus der Norm selbst — für eine Planungshilfe '
          + 'reicht das, für einen Nachweis nicht.'}
        nackt
      >
        <DataTable
          name="Normwerte"
          spalten={[
            {
              schluessel: 'vorgabe', titel: 'Vorgabe', immerSichtbar: true,
              wert: (v: any) => v.schluessel,
              inhalt: (v: any) => <code>{v.schluessel}</code>,
            },
            {
              schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts',
              wert: (v: any) => v.zahlwert,
              inhalt: (v: any) => (v.zahlwert == null
                ? <StatusBadge zustand="warnung" text="offen" klein />
                : <WertEinheit wert={v.zahlwert} einheit={v.einheit ?? undefined} rechts />),
            },
            { schluessel: 'normbezug', titel: 'Normbezug', wert: (v: any) => v.normbezug },
            {
              schluessel: 'begruendung', titel: 'Begründung', wert: (v: any) => v.begruendung,
              inhalt: (v: any) => (
                <span className="sehr-schwach" style={{ fontSize: 12 }}>
                  {v.begruendung ?? '—'}
                </span>
              ),
            },
          ]}
          zeilen={d.vorgaben ?? []}
          schluessel={(v: any) => String(v.schluessel)}
          ausfuhr
          ausfuhrDateiname="melder-normwerte"
          dicht
          leer={(
            <EmptyState
              titel="Keine Vorgabe hinterlegt."
              erklaerung="Ohne Vorgaben rechnet der Lauf mit den Vorgabewerten des Programms."
            />
          )}
        />
      </ContentSection>
    </>
  );
}
